import Image from 'next/image'
import { FunctionComponent, useMemo } from 'react'
import collectionFeatureGifSrc from '@/assets/images/tour/collection-feature.gif'
import Tour, { TourProps, TourStep } from '@/components/common/tour'
import { TabKey } from '@/components/layout/irm/irm-feature-sider-content'
import {
  getMenuTourStepSelector,
  getTourStepSelector,
} from '@/components/tour/utils'
import routes from '@/hooks/use-authorization/routes'
import { useDispatch } from '@/hooks/use-dispatch'
import { useIntl } from '@/i18n/hooks/use-intl'
import { setViewModeForKol } from '@/store/search'
import { setTourStepAction } from '@/store/tour'

const SearchKolTour: FunctionComponent<Omit<TourProps, 'steps'>> = (props) => {
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()

  const steps = useMemo<TourStep[]>(() => {
    return [
      {
        placement: 'right',
        title: formatMessage({
          id: 'workspace:create_step2_usage_purpose_lookingForCandidates',
        }),
        description: formatMessage({
          id: 'tour:description_search_kol_init_menu',
        }),
        target: () =>
          document.querySelector<HTMLElement>(
            getMenuTourStepSelector(TabKey.GeneralSearch),
          ),
      },
      {
        placement: 'bottom',
        title: formatMessage({ id: 'tour:title_search_kol_step_1' }),
        description: formatMessage({
          id: 'tour:description_search_kol_step_1',
        }),
        target: () =>
          document.querySelector<HTMLElement>(
            getTourStepSelector('search-kol', 1),
          ),
        onStart: async (): Promise<void> => {
          dispatch(setViewModeForKol('list'))
          dispatch(setTourStepAction('fill-keyword'))
        },
      },
      {
        placement: 'bottomRight',
        title: formatMessage({ id: 'tour:title_search_kol_step_2' }),
        description: formatMessage({
          id: 'tour:description_search_kol_step_2',
        }),
        target: () =>
          document.querySelector<HTMLElement>(
            getTourStepSelector('search-kol', 2),
          ),
        onStart: (): void => {
          dispatch(setTourStepAction('trigger-search'))
        },
      },
      {
        placement: 'topRight',
        title: formatMessage({ id: 'tour:title_search_kol_step_3' }),
        description: formatMessage({
          id: 'tour:description_search_kol_step_3',
        }),
        target: () =>
          document.querySelector<HTMLElement>(
            getTourStepSelector('search-kol', 3),
          ),
        onStart: (): void => {
          dispatch(setTourStepAction('open-preview-post-list'))
        },
        onNext: (): void => {
          dispatch(setTourStepAction(undefined))
        },
      },
      {
        placement: 'left',
        title: formatMessage({ id: 'tour:title_search_kol_step_4' }),
        description: formatMessage({
          id: 'tour:description_search_kol_step_4',
        }),
        onStart: (): void => {
          dispatch(setTourStepAction('open-directory'))
        },
        target: () =>
          document.querySelector<HTMLElement>(
            getTourStepSelector('search-kol', 4),
          ),
      },
      {
        placement: 'right',
        title: formatMessage({ id: 'tour:title_search_kol_step_5' }),
        description: formatMessage({
          id: 'tour:description_search_kol_step_5',
        }),
        cover: (
          <Image
            alt='collection feature gif'
            height={220}
            src={collectionFeatureGifSrc}
            width={380}
          />
        ),
        target: () =>
          document.querySelector<HTMLElement>(
            getTourStepSelector('search-kol', 5),
          ),
        onNext: async (): Promise<void> => {
          dispatch(setTourStepAction(undefined))
          await routes.postSearch.goto({})
        },
      },
      {
        nextButtonProps: {
          children: 'Next',
        },
        title: formatMessage({ id: 'tour:title_search_kol_step_6' }),
        description: formatMessage({
          id: 'tour:description_search_kol_step_6',
        }),
        target: () =>
          document.querySelector<HTMLElement>(
            getTourStepSelector('search-kol', 6),
          ),
      },
    ]
  }, [dispatch, formatMessage])

  return <Tour {...props} steps={steps} />
}

export default SearchKolTour
